import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import type { BoardSummary, OptionCardView, PlacedBoardConfig } from '../api.js';
import { optionTooltip } from '../optionPayload.js';

/**
 * Building a game.
 *
 * The host places boards, places flags, and sets the rules for this one
 * game.
 *
 * TWO COORDINATE SYSTEMS, and it is worth being explicit. Inside this
 * component boards live in SLOTS -- a 3x3 arrangement of board-sized
 * squares, because that is what is convenient to click. What gets SENT is
 * absolute CELLS: the engine dropped its slot lattice when docking bays
 * arrived, since a 12x4 dock cannot be addressed on a grid of 12x12
 * slots. The conversion happens once, in `layout` below, and nothing
 * else in this file multiplies anything by a board size.
 *
 * A DOCK IS NOT PLACED IN A SLOT. It attaches to an edge of a board the
 * host has already placed, which is what makes it 4 deep against that
 * board rather than floating in a 12-deep slot. Attaching one to the top
 * or left edge would put it at a negative coordinate, so `layout` shifts
 * the whole course to keep the origin at 0,0 -- which is also why flags
 * are stored relative to their slot rather than absolutely. A flag stored
 * absolutely would slide out from under its board the moment a dock was
 * added above it.
 */

const BOARD_CELLS = 12;
const DOCK_DEPTH = 4;
const PREVIEW_PX = 50;

/** How many board slots the host can place into. Three by three is more
 * than any real course and keeps the preview on screen. */
const SLOTS = 3;

type Rotation = 0 | 90 | 180 | 270;

interface PlacedSlot {
  id: string;
  slotX: number;
  slotY: number;
  rotation: Rotation;
}

/**
 * Where the dock goes, as a BAND rather than as an edge of one board.
 *
 * A band is a 4-cell strip lying along a boundary between slot rows (or
 * slot columns), and every board on the far side of it is pushed over by
 * the dock's depth. That is what a dock physically does when you slide one
 * into a course, and it is the only model that gets `board / dock / board`
 * as easily as it gets a dock on an outside edge -- both are just a band,
 * differing only in whether there is a board on one side of it or two.
 *
 * An earlier version of this attached the dock to one named EDGE of one
 * named board. It could do outside edges and nothing else: two boards in
 * adjacent slots leave no gap for a dock, and no choice of edge conjures
 * one.
 *
 * `band` is the boundary index, 0 meaning before the first slot and N
 * meaning after slot N-1. `lane` is which slot column (for a horizontal
 * band) or slot row (for a vertical one) the dock sits in, since a dock is
 * 12 long and spans exactly one.
 */
interface DockChoice {
  id: string;
  axis: 'horizontal' | 'vertical';
  band: number;
  lane: number;
  /** Turn it end for end, moving start 1 to the other side. */
  flip: boolean;
}

/**
 * A flag, stored against the PIECE it was clicked on rather than against
 * an absolute square, so adding a dock above or left of it does not move
 * it onto a different board.
 *
 * A docking bay takes a flag like any other board, which the engine and
 * the server always allowed: `locateOnBoard` scans the dock along with
 * the boards. It has no slot, so a flag on one is anchored to the dock
 * piece's own top-left corner and travels with it as the host moves the
 * band.
 */
type SlotFlag =
  | { number: number; on: 'board'; slotX: number; slotY: number; cx: number; cy: number }
  | { number: number; on: 'dock'; cx: number; cy: number };

interface Piece extends PlacedBoardConfig {
  cellsWide: number;
  cellsHigh: number;
  isDock: boolean;
}

/** A dock is the board in the library that is not square. */
function isDockBoard(b: BoardSummary): boolean {
  return b.width !== b.height;
}

/** Thumbnail height for a palette entry, from the board's OWN aspect.
 * The width is `--palette-thumb` in the stylesheet and this is a calc
 * against it, so resizing the palette is one number in one place. Letting
 * the CSS fix the height too made a 12x4 dock render as a square crop of
 * its middle four columns. */
function thumbStyle(b: BoardSummary) {
  return { height: `calc(var(--palette-thumb) * ${b.height / b.width})` };
}

/**
 * A slot's grid reference: A1, B2, C3. Column letter, row number.
 *
 * Needed because the SAME BOARD CAN BE PLACED TWICE. The dock position
 * buttons named their neighbours by board id alone, so two copies of
 * Chicane3 side by side produced two buttons both reading "Chicane3
 * below" -- different bands, identical labels, no way to tell which was
 * which. The underlying model was never ambiguous: a band is identified
 * by axis, boundary and lane, never by a board id. Only the label was.
 */
function slotRef(slotX: number, slotY: number): string {
  return `${String.fromCharCode(65 + slotX)}${slotY + 1}`;
}

export function NewGame() {
  const navigate = useNavigate();

  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [placed, setPlaced] = useState<PlacedSlot[]>([]);
  const [dock, setDock] = useState<DockChoice | null>(null);
  const [flags, setFlags] = useState<SlotFlag[]>([]);

  const [name, setName] = useState('');
  const [lifeTokens, setLifeTokens] = useState(3);
  const [livesOn, setLivesOn] = useState(true);
  const [timerOn, setTimerOn] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(120);
  // Turn mode's clock, in the units a host thinks of it in.
  const [turnTimerAmount, setTurnTimerAmount] = useState(24);
  const [turnTimerUnit, setTurnTimerUnit] = useState<'minutes' | 'hours'>('hours');
  // How the clock is spent, not how long it runs. 'hand' is the timer
  // this site has always had.
  const [timerMode, setTimerMode] = useState<'hand' | 'last'>('hand');
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [optionsEnabled, setOptionsEnabled] = useState(true);
  // Debris is ON by default, the same as Options: it is a base rule, and
  // the toggle exists for a host who wants it out of the way.
  const [debrisEnabled, setDebrisEnabled] = useState(true);
  // Open by default, which is how every game behaved before it was a
  // choice. A late joiner comes in level with whoever is last, so the
  // toggle is for a table that wants the door shut rather than a rule
  // that needed fixing.
  const [lateJoin, setLateJoin] = useState(true);
  const [startingOptions, setStartingOptions] = useState(0);
  const [catalog, setCatalog] = useState<OptionCardView[]>([]);
  /** Which Options are in the deck. Everything, until the host says
   * otherwise. */
  const [chosenOptions, setChosenOptions] = useState<string[] | null>(null);
  // Normal mode is the game as it always was. Turn mode makes every
  // decision while programming, so it has no clock and its own cards.
  const [turnMode, setTurnMode] = useState(false);
  /** The cards this mode deals from: normal mode's are the catalog less
   * the turn cards; turn mode's are the turn cards and every card that
   * never interrupts. The server's `optionIdsForMode` draws the same line. */
  const modeCards = useMemo(
    () => catalog.filter((o) => (turnMode ? o.turnMode || !o.interrupts : !o.turnMode)),
    [catalog, turnMode],
  );
  // Turn mode has fewer cards, so a count chosen in normal mode can be
  // more than it holds, which the server refuses.
  useEffect(() => {
    if (modeCards.length > 0) setStartingOptions((n) => Math.min(n, modeCards.length));
  }, [modeCards]);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [result, options] = await Promise.all([
          api.boards(), api.optionCatalog(),
        ]);
        setBoards(result.boards);
        setSelected(result.boards.find((b) => !isDockBoard(b))?.id ?? null);
        // Alphabetical: the catalog's own order is the order cards were
        // added, which means nothing to a host looking for one.
        setCatalog(
          [...options.options].sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load the boards.');
      }
    })();
  }, []);

  const courseBoards = useMemo(() => boards.filter((b) => !isDockBoard(b)), [boards]);

  // One flat list, ordered by expansion and then by name. A board with no
  // expansion shows a dash, which sorts it to the front.
  const sortedBoards = useMemo(
    () => [...courseBoards].sort((a, b) => (
      (a.expansion ?? '-').localeCompare(b.expansion ?? '-')
      || a.id.localeCompare(b.id)
    )),
    [courseBoards],
  );
  const dockBoards = useMemo(() => boards.filter(isDockBoard), [boards]);
  const boardById = useMemo(
    () => new Map(boards.map((b) => [b.id, b])), [boards],
  );

  const boardAt = (sx: number, sy: number) =>
    placed.find((p) => p.slotX === sx && p.slotY === sy);

  /**
   * How far a slot is pushed over by the dock band, in cells.
   *
   * A band lies on a boundary, so every slot at or beyond that boundary
   * moves by the dock's depth and everything before it stays put. Whole
   * ROWS move, not just the lane the dock sits in: boards are pushed apart
   * by a dock exactly as they would be on a table, and shifting only the
   * dock's own column would leave the boards beside it half a board out of
   * line with their neighbours.
   *
   * The consequence is visible in the preview and is deliberate: a dock
   * slid between two boards of a two-column course leaves a 4-cell VOID
   * band beside it, which is what the empty table would be.
   */
  const offsetFor = (slotX: number, slotY: number) => ({
    x: dock?.axis === 'vertical' && slotX >= dock.band ? DOCK_DEPTH : 0,
    y: dock?.axis === 'horizontal' && slotY >= dock.band ? DOCK_DEPTH : 0,
  });

  /**
   * Everything in absolute cells, which is the only place slots turn into
   * the coordinates the server is given.
   */
  const layout = useMemo(() => {
    const pieces: Piece[] = placed.map((p) => {
      const off = offsetFor(p.slotX, p.slotY);
      return {
        id: p.id,
        gridX: p.slotX * BOARD_CELLS + off.x,
        gridY: p.slotY * BOARD_CELLS + off.y,
        rotation: p.rotation,
        cellsWide: BOARD_CELLS,
        cellsHigh: BOARD_CELLS,
        isDock: false,
      };
    });

    if (dock) {
      const horizontal = dock.axis === 'horizontal';
      // Which side of the band has a board on it decides which way the
      // dock faces. With boards on BOTH sides either long edge is a legal
      // start side, so the flip is the host's to choose; with a board on
      // only one, the base rotation already points the right way and the
      // flip turns it away, which is still legal and still their choice.
      const before = horizontal
        ? placed.some((p) => p.slotY === dock.band - 1 && p.slotX === dock.lane)
        : placed.some((p) => p.slotX === dock.band - 1 && p.slotY === dock.lane);

      // The docking bay files are authored with their board-facing edge at
      // the BOTTOM. A quarter turn clockwise carries that edge to the
      // WEST, a half turn to the NORTH, three quarters to the EAST.
      //
      // `before` means the board is on the low side of the band -- above
      // it, or to its left -- so the dock is BELOW or to the RIGHT of that
      // board and has to face back up or back left toward it.
      //
      // The vertical pair was inverted here at first and the type checker
      // could not care less: both values are valid rotations. It was found
      // by composing all four arrangements and asking where start 1
      // actually landed, which put it on the FAR side of the dock from the
      // board it is supposed to launch onto.
      const base: Rotation = horizontal
        ? (before ? 180 : 0)
        : (before ? 90 : 270);

      pieces.push({
        id: dock.id,
        gridX: (horizontal ? dock.lane : dock.band) * BOARD_CELLS,
        gridY: (horizontal ? dock.band : dock.lane) * BOARD_CELLS,
        rotation: ((base + (dock.flip ? 180 : 0)) % 360) as Rotation,
        cellsWide: horizontal ? BOARD_CELLS : DOCK_DEPTH,
        cellsHigh: horizontal ? DOCK_DEPTH : BOARD_CELLS,
        isDock: true,
      });
    }

    // Nothing is ever negative: a band at boundary 0 pushes everything
    // else off the origin rather than sitting behind it.
    const width = Math.max(0, ...pieces.map((p) => p.gridX + p.cellsWide));
    const height = Math.max(0, ...pieces.map((p) => p.gridY + p.cellsHigh));

    return { pieces, width, height };
  }, [placed, dock]);

  /** Where a slot's top-left square sits in absolute cells. */
  const slotOrigin = (sx: number, sy: number) => {
    const off = offsetFor(sx, sy);
    return { x: sx * BOARD_CELLS + off.x, y: sy * BOARD_CELLS + off.y };
  };

  const absoluteFlags = useMemo(() => flags.flatMap((f) => {
    if (f.on === 'dock') {
      // A dock flag whose piece has gone yields nothing rather than a
      // coordinate off the origin. The effect below clears these, but a
      // render happens first.
      const piece = layout.pieces.find((p) => p.isDock);
      if (!piece) return [];
      return [{ number: f.number, x: piece.gridX + f.cx, y: piece.gridY + f.cy }];
    }
    const origin = slotOrigin(f.slotX, f.slotY);
    return [{ number: f.number, x: origin.x + f.cx, y: origin.y + f.cy }];
  }), [flags, dock, layout]);

  /**
   * Every band a dock could go in, given what is placed.
   *
   * A band is offered where it would touch at least one board, which is
   * what stops a dock floating in the void two slots away from anything.
   * Where it touches two, that is the `board / dock / board` case and
   * needs no special handling at all -- it is the same band, and the only
   * difference is what the label says.
   */
  const dockSpots = useMemo(() => {
    const at = (sx: number, sy: number) =>
      placed.find((p) => p.slotX === sx && p.slotY === sy);
    const spots: {
      axis: 'horizontal' | 'vertical'; band: number; lane: number; label: string;
    }[] = [];

    for (const axis of ['horizontal', 'vertical'] as const) {
      const horizontal = axis === 'horizontal';
      for (let lane = 0; lane < SLOTS; lane++) {
        for (let band = 0; band <= SLOTS; band++) {
          const before = horizontal ? at(lane, band - 1) : at(band - 1, lane);
          const after = horizontal ? at(lane, band) : at(band, lane);
          if (!before && !after) continue;
          // Every label carries a grid reference, so two copies of one
          // board cannot produce two buttons that read the same.
          const refBefore = before ? slotRef(before.slotX, before.slotY) : '';
          const refAfter = after ? slotRef(after.slotX, after.slotY) : '';
          const label = before && after
            ? `${refBefore} ${before.id} / ${refAfter} ${after.id}`
            : before
              ? `${refBefore} ${before.id}, ${horizontal ? 'below' : 'right'}`
              : `${refAfter} ${after!.id}, ${horizontal ? 'above' : 'left'}`;
          spots.push({ axis, band, lane, label });
        }
      }
    }
    return spots;
  }, [placed]);

  /** Flags are numbered 1 upwards with no gaps: the engine walks them in
   * order and the highest wins the game. */
  const renumber = (list: SlotFlag[]): SlotFlag[] => [...list]
    .sort((a, b) => a.number - b.number)
    .map((f, i) => ({ ...f, number: i + 1 }));

  /**
   * Whether a band has a board on either side of it.
   *
   * A dock is a strip between or beside boards; with nothing on either
   * side it is a floating island in the void, which is not a course.
   */
  const bandTouchesABoard = (d: DockChoice, boards: PlacedSlot[]) => (
    d.axis === 'horizontal'
      ? boards.some((p) => p.slotX === d.lane
        && (p.slotY === d.band - 1 || p.slotY === d.band))
      : boards.some((p) => p.slotY === d.lane
        && (p.slotX === d.band - 1 || p.slotX === d.band))
  );

  /**
   * Drop a dock that has been orphaned.
   *
   * Written as an effect on `placed` rather than as a step inside the
   * removal handler, deliberately. The handler version only covered the
   * one path that happened to exist, so any future way of changing the
   * boards would have reintroduced exactly this bug. Here it holds no
   * matter what moved the boards.
   *
   * Dropped rather than re-anchored: guessing which of the remaining
   * boards the host meant is worse than making them say.
   */
  useEffect(() => {
    setDock((d) => (d && !bandTouchesABoard(d, placed) ? null : d));
  }, [placed]);

  /**
   * Drop a flag the dock no longer has a square for.
   *
   * A dock flag is anchored to the piece, so sliding the band along
   * carries it with the dock and nothing needs doing. Two changes do
   * strand one: removing the dock, and turning a horizontal band into a
   * vertical one, which makes a 12x4 strip 4x12 and leaves anything past
   * its fourth column off the piece entirely.
   *
   * Written as an effect on the layout rather than into each of the four
   * handlers that can move a dock, for the reason the orphan check above
   * gives: a rule that lives in the handlers only covers the paths that
   * happen to exist today.
   */
  useEffect(() => {
    const piece = layout.pieces.find((p) => p.isDock);
    setFlags((current) => {
      const kept = current.filter((f) => f.on !== 'dock'
        || (piece !== undefined && f.cx < piece.cellsWide && f.cy < piece.cellsHigh));
      // The same array back when nothing went, or this re-renders for ever.
      return kept.length === current.length ? current : renumber(kept);
    });
  }, [layout]);

  const place = (sx: number, sy: number) => {
    const existing = boardAt(sx, sy);
    if (!existing) {
      if (!selected) return;
      setPlaced((current) => [
        ...current, { id: selected, slotX: sx, slotY: sy, rotation: 0 },
      ]);
      return;
    }

    // Clicking a placed board turns it a quarter turn, and off the board
    // entirely after a full rotation.
    const next = ((existing.rotation + 90) % 360) as Rotation;
    if (next !== 0) {
      setPlaced((current) => current.map(
        (p) => (p === existing ? { ...p, rotation: next } : p),
      ));
      return;
    }

    // Removal.
    //
    // This used to set a `removed` flag INSIDE a `setPlaced` updater and
    // read it on the line after, which is a race. React only runs an
    // updater eagerly when the fiber has no work pending; otherwise it
    // runs during the next render, long after the `if` has been evaluated
    // against a variable still holding `false`. StrictMode's double
    // invocation muddies it further. The visible symptom was a docking bay
    // left orphaned in the void after the board it hung off was removed --
    // and, less visibly, flags stranded on a board that no longer exists,
    // since both cleanups sat behind the same flag.
    //
    // Nothing is flagged now. The surviving boards are computed from
    // `placed` in the handler, where they are simply known.
    const rest = placed.filter((p) => p !== existing);
    setPlaced(rest);
    setFlags((current) => renumber(
      current.filter((f) => f.on !== 'board' || f.slotX !== sx || f.slotY !== sy),
    ));
  };

  const toggleFlag = (absX: number, absY: number) => {
    // Which slot an absolute square belongs to is no longer a division:
    // the dock band pushes rows and columns over by 4, so the same square
    // is on a different slot depending on where the dock is. Asked of the
    // placed boards instead, which is the only thing that knows.
    const found = placed.find((p) => {
      const origin = slotOrigin(p.slotX, p.slotY);
      return absX >= origin.x && absX < origin.x + BOARD_CELLS
        && absY >= origin.y && absY < origin.y + BOARD_CELLS;
    });

    // The dock, which takes a flag like any other board. Its footprint is
    // read off the laid-out piece rather than a constant, since a 12x4
    // strip is 4x12 in a vertical band.
    const dockPiece = layout.pieces.find((p) => p.isDock);
    if (!found && dockPiece
      && absX >= dockPiece.gridX && absX < dockPiece.gridX + dockPiece.cellsWide
      && absY >= dockPiece.gridY && absY < dockPiece.gridY + dockPiece.cellsHigh) {
      const cx = absX - dockPiece.gridX;
      const cy = absY - dockPiece.gridY;
      const onDock = flags.find(
        (f) => f.on === 'dock' && f.cx === cx && f.cy === cy,
      );
      if (onDock) {
        setFlags((current) => renumber(current.filter((f) => f !== onDock)));
        return;
      }
      setFlags((current) => [
        ...current, { number: current.length + 1, on: 'dock', cx, cy },
      ]);
      return;
    }

    if (!found) return; // neither a board nor the dock: void
    const { slotX, slotY } = found;
    const origin = slotOrigin(slotX, slotY);
    const cx = absX - origin.x;
    const cy = absY - origin.y;
    const existing = flags.find(
      (f) => f.on === 'board' && f.slotX === slotX && f.slotY === slotY
        && f.cx === cx && f.cy === cy,
    );
    if (existing) {
      setFlags((current) => renumber(current.filter((f) => f !== existing)));
      return;
    }
    setFlags((current) => [
      ...current, { number: current.length + 1, on: 'board', slotX, slotY, cx, cy },
    ]);
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const dockPiece = layout.pieces.find((p) => p.isDock);
      const { gameId } = await api.createGame({
        course: {
          name: name.trim() || 'Untitled course',
          boards: layout.pieces
            .filter((p) => !p.isDock)
            .map(({ id, gridX, gridY, rotation }) => ({ id, gridX, gridY, rotation })),
          dock: dockPiece
            ? {
              id: dockPiece.id,
              gridX: dockPiece.gridX,
              gridY: dockPiece.gridY,
              rotation: dockPiece.rotation,
            }
            : null,
          flags: absoluteFlags,
          lifeTokens: livesOn ? lifeTokens : 0,
        },
        timerSeconds: !timerOn ? null
          : turnMode ? turnTimerAmount * (turnTimerUnit === 'hours' ? 3600 : 60)
            : timerSeconds,
        timerMode,
        turnMode,
        maxPlayers,
        optionsEnabled,
        debrisEnabled,
        lateJoin,
        // Only meaningful with Options in play at all.
        startingOptions: optionsEnabled ? startingOptions : 0,
        // Omitted entirely when the host left the list alone, which the
        // server reads as every card of the mode.
        ...(chosenOptions ? { optionIds: chosenOptions } : {}),
      });
      await api.joinGame(gameId);
      navigate(`/games/${gameId}`);
    } catch (err) {
      // Nothing is cleared: the boards and flags stay exactly as placed so
      // the host can correct whatever the server objected to. The message
      // says what is wrong; scrolling to it makes sure it is seen, since
      // the button is at the bottom of a long screen.
      setError(err instanceof ApiError ? err.message : 'Could not create the game.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  // With no docking bay everyone starts on flag 1, so one flag is also
  // the winning flag and the game is over on turn 1. The button stays
  // greyed out rather than the server refusing it after the fact.
  const ready = placed.length > 0
    && flags.length > 0
    && (dock !== null || flags.length > 1);

  // The same checks the server makes, so the host is told before
  // submitting rather than after. The server still enforces them; this is
  // only earlier notice.
  const warnings: string[] = [];
  if (flags.length > 0 && !flags.some((f) => f.number === 1)) {
    warnings.push('There is no flag 1.');
  }
  if (!dock && flags.some((f) => f.number === 1)) {
    warnings.push('With no docking bay, flag 1 is where everyone starts.');
  }
  if (dock) {
    warnings.push(
      'Robots start on the docking bay, one to a position, chosen at random '
      + 'from the eight.',
    );
  }

  /** One piece drawn in the preview. The image is its natural way round
   * and is spun about its own centre, so a quarter-turned dock is placed
   * by matching centres rather than corners -- the same arithmetic
   * `Board.tsx` does for the same reason. */
  const pieceStyle = (p: Piece) => {
    const transposed = p.rotation === 90 || p.rotation === 270;
    const naturalW = transposed ? p.cellsHigh : p.cellsWide;
    const naturalH = transposed ? p.cellsWide : p.cellsHigh;
    const centreX = (p.gridX + p.cellsWide / 2) * PREVIEW_PX;
    const centreY = (p.gridY + p.cellsHigh / 2) * PREVIEW_PX;
    return {
      position: 'absolute' as const,
      left: centreX - (naturalW * PREVIEW_PX) / 2,
      top: centreY - (naturalH * PREVIEW_PX) / 2,
      width: naturalW * PREVIEW_PX,
      height: naturalH * PREVIEW_PX,
      transform: p.rotation ? `rotate(${p.rotation}deg)` : undefined,
    };
  };

  return (
    <main className="content wide stack" style={{ gap: '2rem' }}>
      <div className="spread">
        <div>
          <p className="eyebrow">New game</p>
          <h2>Build the course</h2>
        </div>
        <button className="secondary" onClick={() => navigate('/')}>Back to lobby</button>
      </div>

      {error && <p className="notice">{error}</p>}

      <section className="stack">
        <p className="eyebrow">1. Choose a board, then click a slot to place it</p>

        {courseBoards.length === 0 ? (
          <p className="muted">
            No boards have been imported. Import a board.json before building a course.
          </p>
        ) : (
          <div className="board-palette">
            {sortedBoards.map((board) => (
              <button
                key={board.id}
                className={`palette-item${selected === board.id ? ' chosen' : ''}`}
                onClick={() => setSelected(board.id)}
              >
                <img src={board.image} alt="" style={thumbStyle(board)} />
                <span className="mono">{board.id}</span>
                <span className="palette-expansion">{board.expansion ?? '-'}</span>
              </button>
            ))}
          </div>
        )}

        <div className="slot-grid">
          {Array.from({ length: SLOTS * SLOTS }, (_, i) => {
            const sx = i % SLOTS;
            const sy = Math.floor(i / SLOTS);
            const here = boardAt(sx, sy);
            return (
              <button
                key={i}
                className={`board-slot${here ? ' filled' : ''}`}
                onClick={() => place(sx, sy)}
                title={here
                  ? `${here.id}, rotated ${here.rotation} degrees. Click to turn, or turn past 270 to remove.`
                  : 'Click to place the chosen board'}
              >
                {here ? (
                  <>
                    <img
                      src={boardById.get(here.id)?.image}
                      alt=""
                      style={{ transform: `rotate(${here.rotation}deg)` }}
                    />
                    {/* The grid reference leads, because it is what the
                        docking bay position buttons name -- and with the
                        same board placed twice, the id alone does not say
                        which one is meant. */}
                    <span className="slot-tag mono">
                      {slotRef(sx, sy)} {here.id} {here.rotation ? `${here.rotation}\u00B0` : ''}
                    </span>
                  </>
                ) : (
                  <span className="muted mono">{slotRef(sx, sy)}</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {dockBoards.length > 0 && (
        <section className="stack">
          <p className="eyebrow">
            2. Docking bay - optional, and where robots start if there is one
          </p>

          {/* Shown whether or not a board is placed yet. It was gated on
              `placed.length > 0` and that read as the feature being
              missing: docking bays are deliberately kept out of the board
              palette above, so with nothing placed there was no docking
              bay ANYWHERE on the screen and no hint that placing a board
              would produce one. A dock still needs a board to lean
              against -- it is a band between or beside boards -- so the
              picker says so rather than offering nowhere to put it. */}
          {placed.length === 0 && (
            <p className="muted" style={{ fontSize: '0.8125rem' }}>
              Place a board first - a docking bay attaches to one.
            </p>
          )}

          <div className="board-palette">
            {dockBoards.map((board) => (
              <button
                key={board.id}
                className={`palette-item${dock?.id === board.id ? ' chosen' : ''}`}
                disabled={placed.length === 0}
                onClick={() => setDock((current) => {
                  if (current?.id === board.id) return null;
                  const spot = dockSpots[0];
                  if (!spot) return null;
                  const { axis, band, lane } = spot;
                  return { id: board.id, axis, band, lane, flip: false };
                })}
              >
                <img src={board.image} alt="" style={thumbStyle(board)} />
                <span className="mono">
                  {board.id} {board.width}x{board.height}
                </span>
              </button>
            ))}
          </div>

          {dock && (
            <div className="stack" style={{ gap: '0.3rem' }}>
              <p className="eyebrow">Where it goes</p>
              <div className="row" style={{ flexWrap: 'wrap', gap: '0.3rem' }}>
                {dockSpots.map((spot) => {
                  const on = dock.axis === spot.axis
                    && dock.band === spot.band && dock.lane === spot.lane;
                  return (
                    <button
                      key={`${spot.axis},${spot.band},${spot.lane}`}
                      className={on ? '' : 'secondary'}
                      onClick={() => setDock({
                        ...dock, axis: spot.axis, band: spot.band, lane: spot.lane,
                      })}
                    >
                      {spot.label}
                    </button>
                  );
                })}
              </div>
              <label className="powerdown">
                <input
                  type="checkbox"
                  checked={dock.flip}
                  onChange={(e) => setDock({ ...dock, flip: e.target.checked })}
                />
                <span>
                  Turn it end for end
                  <span className="muted">
                    {' '}
                    - either long edge of a docking bay may face the course;
                    this moves start 1 to the other side
                  </span>
                </span>
              </label>
            </div>
          )}
        </section>
      )}

      {/* The heading is always here, and only the canvas waits for a
          board. It was the whole section that was gated, so a host who
          had placed nothing yet read 1, 2, then 4 and had to wonder what
          step 3 was and why it had been skipped. Same reasoning as the
          docking bay above, which came out of its gate for the same
          reason. */}
      <section className="stack">
        <p className="eyebrow">
          3. Click squares to place flags - click a flag again to remove it
        </p>
        {placed.length === 0 ? (
          <p className="muted">
            Place a board above and its squares appear here to click.
          </p>
        ) : (
          <>
          <div
            className="flag-canvas"
            style={{ width: layout.width * PREVIEW_PX, height: layout.height * PREVIEW_PX }}
          >
            {layout.pieces.map((p, i) => (
              <img
                key={`${p.id}-${i}`}
                src={boardById.get(p.id)?.image}
                alt=""
                style={pieceStyle(p)}
              />
            ))}

            {Array.from({ length: layout.height }, (_, y) => (
              Array.from({ length: layout.width }, (_, x) => {
                const flag = absoluteFlags.find((f) => f.x === x && f.y === y);
                const piece = layout.pieces.find((p) => (
                  x >= p.gridX && x < p.gridX + p.cellsWide
                  && y >= p.gridY && y < p.gridY + p.cellsHigh
                ));
                // "off" is the void: outside every piece. A docking bay
                // takes a flag like any other board.
                const placeable = Boolean(piece);
                return (
                  <button
                    key={`${x},${y}`}
                    className={`flag-cell${flag ? ' has-flag' : ''}${placeable ? '' : ' off'}`}
                    style={{
                      position: 'absolute',
                      left: x * PREVIEW_PX,
                      top: y * PREVIEW_PX,
                      width: PREVIEW_PX,
                      height: PREVIEW_PX,
                    }}
                    onClick={() => toggleFlag(x, y)}
                    title={flag ? `Flag ${flag.number}` : `${x},${y}`}
                  >
                    {flag ? flag.number : ''}
                  </button>
                );
              })
            ))}
          </div>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            Flags are numbered in the order you place them. The last one wins the game.
            Squares outside every board are void - not floor, and not a pit:
            a robot that drives into one is destroyed.
          </p>
          </>
        )}
      </section>

      <section className="stack">
        <p className="eyebrow">4. Settings</p>

        <div className="settings">
          <div>
            <label htmlFor="course-name">Course name</label>
            <input
              id="course-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled course"
            />
          </div>

          <div>
            <label htmlFor="players">Maximum players</label>
            <input
              id="players"
              type="number"
              min={1}
              max={8}
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
            />
          </div>

          <div>
            <label>Life tokens</label>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={livesOn}
                onChange={(e) => setLivesOn(e.target.checked)}
              />
              <span>
                Use life tokens
                <span className="muted"> - off means robots always come back and deaths are only counted</span>
              </span>
            </label>
            {livesOn && (
              <input
                type="number"
                min={1}
                max={9}
                value={lifeTokens}
                onChange={(e) => setLifeTokens(Number(e.target.value))}
                style={{ marginTop: '0.4rem' }}
              />
            )}
          </div>

          <div>
            <label>Game mode</label>
            <div className="stack" style={{ gap: '0.2rem' }}>
              <label className="powerdown">
                <input
                  type="radio"
                  name="game-mode"
                  checked={!turnMode}
                  onChange={() => { setTurnMode(false); setChosenOptions(null); }}
                />
                <span>
                  Normal
                  <span className="muted">
                    {' '}
                    - the game as printed: players can be interrupted mid-turn
                    for decisions.
                  </span>
                </span>
              </label>
              <label className="powerdown">
                <input
                  type="radio"
                  name="game-mode"
                  checked={turnMode}
                  onChange={() => { setTurnMode(true); setChosenOptions(null); }}
                />
                <span>
                  Turn
                  <span className="muted">
                    {' '}
                    - every decision is made while programming; the turn runs
                    straight through and is played back. Decisions that
                    would stop a register are pre-set, determined while
                    programming, or left out.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label>Option cards</label>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={optionsEnabled}
                onChange={(e) => setOptionsEnabled(e.target.checked)}
              />
              <span>Use Option cards</span>
            </label>

            {/* Which cards are in the deck. All of them unless the host
                says otherwise, so a host who does not care need not
                read the list. */}
            {optionsEnabled && modeCards.length > 0 && (
              <div className="option-picker">
                <div className="spread" style={{ marginBottom: '0.35rem' }}>
                  <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                    {(chosenOptions ?? modeCards.map((o) => o.id)).length} of {modeCards.length} in the deck
                  </span>
                  <div className="row" style={{ gap: '0.3rem' }}>
                    <button
                      className="secondary tiny"
                      onClick={() => setChosenOptions(null)}
                    >
                      All
                    </button>
                    {/* Turn mode's cards never interrupt, so there is
                        nothing for this to take out. */}
                    {!turnMode && (
                      <button
                        className="secondary tiny"
                        title="Only the options that never pause a register to ask a question"
                        onClick={() => setChosenOptions(
                          modeCards.filter((o) => !o.interrupts).map((o) => o.id),
                        )}
                      >
                        Non-Interrupt
                      </button>
                    )}
                    <button
                      className="secondary tiny"
                      onClick={() => setChosenOptions([])}
                    >
                      None
                    </button>
                  </div>
                </div>

                {modeCards.map((option) => {
                  const on = (chosenOptions ?? modeCards.map((o) => o.id)).includes(option.id);
                  return (
                    <label key={option.id} className="option-pick" title={optionTooltip(option)}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          const current = chosenOptions ?? modeCards.map((o) => o.id);
                          setChosenOptions(on
                            ? current.filter((id) => id !== option.id)
                            : [...current, option.id]);
                        }}
                      />
                      <span>{option.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {optionsEnabled && (
            <div>
              <label htmlFor="starting-options">Options to start with</label>
              <input
                id="starting-options"
                type="number"
                min={0}
                // The mode's cards, which is the most a robot could be dealt.
                // Undefined until it has loaded: the server has the real
                // ceiling, and a max of 0 in the meantime would clamp
                // whatever the host typed down to nothing.
                max={modeCards.length || undefined}
                value={startingOptions}
                onChange={(e) => setStartingOptions(Math.max(0, Math.min(
                  modeCards.length || Number.MAX_SAFE_INTEGER,
                  Number(e.target.value) || 0,
                )))}
              />
              <span className="muted">
                {' '}
                Number of options dealt to every robot when the game starts.
                0 is the ordinary game.
              </span>
            </div>
          )}

          <div>
            <label>Debris</label>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={debrisEnabled}
                onChange={(e) => setDebrisEnabled(e.target.checked)}
              />
              <span>
                Use debris
                {/* The rule VERBATIM.
                    Not to be reworded or trimmed to fit: a host reading a
                    paraphrase has to guess at the thresholds, and the two
                    that matter here -- 2 or more damage, and enters AND
                    exits within one register phase -- are exactly what a
                    summary drops. */}
                <span className="muted">
                  {' '}
                  - Whenever a robot is destroyed or receives 2 or more points
                  of damage during a register phase, a debris token will appear
                  in that robot&apos;s square. A robot that enters and exits a
                  square with debris during a single register phase receives a
                  point of damage. Movement includes being pushed or sliding.
                  Debris is removed immediately after causing damage.
                </span>
              </span>
            </label>
          </div>

          <div>
            <label>Joining</label>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={lateJoin}
                onChange={(e) => setLateJoin(e.target.checked)}
              />
              <span>
                Let players join after the game starts
                <span className="muted">
                  {' '}
                  - a latecomer comes in on the last flag every player has
                  tagged, or on the start track if anyone has yet to reach
                  flag 1. When disabled no one can join the game after it
                  starts.
                </span>
              </span>
            </label>
          </div>

          <div>
            <label>Programming timer</label>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={timerOn}
                onChange={(e) => setTimerOn(e.target.checked)}
              />
              <span>
                Use a timer
                <span className="muted"> - off means programming waits as long as it takes. Ignored for Turn 1</span>
              </span>
            </label>
            {timerOn && (
              <>
                {/* The box is a bare number, so the unit has to be stated or
                    120 reads as easily as minutes as seconds. */}
                {/* Turn mode's turns can be taken hours apart, so its clock
                    is set in minutes or hours, up to a week. */}
                {turnMode ? (
                  <div className="row" style={{ marginTop: '0.4rem', alignItems: 'center' }}>
                    <input
                      id="timer-amount"
                      type="number"
                      min={1}
                      max={turnTimerUnit === 'hours' ? 168 : 10080}
                      value={turnTimerAmount}
                      onChange={(e) => setTurnTimerAmount(Number(e.target.value))}
                    />
                    <select
                      aria-label="unit"
                      value={turnTimerUnit}
                      onChange={(e) => setTurnTimerUnit(e.target.value as 'minutes' | 'hours')}
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                    </select>
                  </div>
                ) : (
                  <div className="row" style={{ marginTop: '0.4rem', alignItems: 'center' }}>
                    <label htmlFor="timer-seconds" className="muted">seconds</label>
                    <input
                      id="timer-seconds"
                      type="number"
                      min={10}
                      max={600}
                      step={5}
                      value={timerSeconds}
                      onChange={(e) => setTimerSeconds(Number(e.target.value))}
                    />
                  </div>
                )}
                {/* WHEN the seconds start, which is a different question
                    from how many there are. Radios rather than a second
                    checkbox: the two are alternatives, and a checkbox
                    labelled "instead start it on the last player" reads
                    as something you can have as well. */}
                <div className="stack" style={{ marginTop: '0.4rem', gap: '0.2rem' }}>
                  <label className="powerdown">
                    <input
                      type="radio"
                      name="timer-mode"
                      checked={timerMode === 'hand'}
                      onChange={() => setTimerMode('hand')}
                    />
                    <span>
                      From the start of programming
                      <span className="muted">
                        {' '}
                        - the whole table has that long to program the turn.
                      </span>
                    </span>
                  </label>
                  <label className="powerdown">
                    <input
                      type="radio"
                      name="timer-mode"
                      checked={timerMode === 'last'}
                      onChange={() => setTimerMode('last')}
                    />
                    <span>
                      From when everyone but one has finished
                      <span className="muted">
                        {' '}
                        - the moment only one player is left to program, they
                        have that long. In a game of four, the third player
                        to finish starts the fourth player&apos;s clock; in a
                        game of two, the first to finish starts the
                        other&apos;s.
                      </span>
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {warnings.map((warning) => (
        <p key={warning} className="notice ok">{warning}</p>
      ))}

      <div className="row">
        <button onClick={() => void create()} disabled={!ready || busy}>
          Create the game
        </button>
        {!ready && (
          <span className="muted mono" style={{ fontSize: '0.8125rem' }}>
            {placed.length > 0 && flags.length > 0
              ? 'With no docking bay everyone starts on flag 1, so place a second flag.'
              : 'Place at least one board and one flag.'}
          </span>
        )}
      </div>
    </main>
  );
}
